/**
 * AccommodationList Component
 * 
 * A React component that displays and manages a list of accommodation items for a specific trip.
 * Provides functionality for viewing, adding, editing, and deleting accommodation entries.
 * 
 * Features:
 * - Displays accommodation items in a grid layout
 * - Supports CRUD operations (Create, Read, Update, Delete)
 * - Shows accommodation type icons
 * - Displays date ranges and status
 * - Responsive design with CSS Grid
 * 
 * Component Structure:
 * - Header with "Add Accommodation" button
 * - Grid of accommodation cards
 * - Modal for adding/editing accommodation
 * - Empty state display when no items exist
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth, useTrip, getPlaceById, eventBridgeService, EVENT_TYPES, connectionRegistryService } from '@trip-planner-monorepo/shared';
import { accommodationService } from '@trip-planner-monorepo/accommodation/services/accommodationService';
import AddAccommodation from '../AddAccommodation/AddAccommodation';
import { FaPlus, FaTrash, FaEdit, FaHotel, FaHome, FaBuilding, FaUmbrellaBeach, FaBed, FaQuestion } from 'react-icons/fa';
import './AccommodationList.css';
import '@trip-planner-monorepo/shared/components/styles/actionButtons.css';

function AccommodationList() {
  // State Management
  const [accommodation, setAccommodation] = useState([]); // Stores all accommodation items
  const [loading, setLoading] = useState(true);          // Controls loading state
  const [error, setError] = useState('');                // Stores error messages
  const [showAddModal, setShowAddModal] = useState(false); // Controls add/edit modal visibility
  const [editingAccommodation, setEditingAccommodation] = useState(null); // Stores item being edited
  const [placesData, setPlacesData] = useState({}); // Stores place data for linked accommodations

  // Authentication and Route Parameters
  const { currentUser } = useAuth();
  const { effectiveUserId } = useTrip();
  const { tripId } = useParams();

  /**
     * Fetches all accommodation items for the current trip
     * Called on component mount and after CRUD operations
     */
  const fetchAccommodation = async () => {
    if (!currentUser || !tripId) return;

    setLoading(true);
    try {
      console.log('Fetching accommodation items...');
      const fetchedAccommodation = await accommodationService.fetchAccommodation(effectiveUserId, tripId);
      console.log('Fetched accommodation items:', fetchedAccommodation);
      setAccommodation(fetchedAccommodation);

      // Fetch place data for accommodations with place references
      await fetchPlacesData(fetchedAccommodation);
    } catch (err) {
      setError('Failed to load accommodation items. Please try again.');
      console.error('Error fetching accommodation:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetches place data for accommodations that are linked to places
   * @param {Array} accommodationItems - Array of accommodation items
   */
  const fetchPlacesData = async (accommodationItems) => {
    if (!currentUser || !tripId) return;

    const newPlacesData = {};

    // On registry_only / dual_write trips the legacy placeReference may be absent; resolve
    // the located_in place from the registry so the list still shows the linked place.
    try {
      const displayIndex = await connectionRegistryService.getRegistryDisplayIndex(effectiveUserId, tripId);
      accommodationItems.forEach(item => {
        if (!item.placeReference?.placeId) {
          const placeId = displayIndex.accommodationPlaceId.get(item.id);
          if (placeId) item.placeReference = { placeId };
        }
      });
    } catch (regError) {
      console.warn('AccommodationList: could not resolve places from registry', regError);
    }

    // Get unique place IDs from accommodations with place references
    const placeIds = [...new Set(
      accommodationItems
        .filter(item => item.placeReference?.placeId)
        .map(item => item.placeReference.placeId)
    )];

    // Fetch place data for each unique place ID
    await Promise.all(
      placeIds.map(async (placeId) => {
        try {
          const place = await getPlaceById(effectiveUserId, tripId, placeId);
          if (place) {
            newPlacesData[placeId] = place;
          }
        } catch (error) {
          console.error(`Error fetching place ${placeId}:`, error);
        }
      })
    );

    setPlacesData(newPlacesData);
  };

  // Initial data fetch on component mount
  useEffect(() => {
    // Subscribe to accommodation city changes to refresh the list
    const cityChangeSubscription = eventBridgeService.subscribe(EVENT_TYPES.ACCOMMODATION_CITY_CHANGED, (eventData) => {
      console.log('AccommodationList: Accommodation city changed, refreshing list...');
      fetchAccommodation();
    });

    // Subscribe to place linking events to refresh the list
    const placeLinkSubscription = eventBridgeService.subscribe(EVENT_TYPES.ACCOMMODATION_PLACE_LINKED, (eventData) => {
      console.log('AccommodationList: Accommodation linked to place, refreshing list...');
      fetchAccommodation();
    });

    // Subscribe to suggestion acceptance to refresh the list
    const suggestionProcessedSubscription = eventBridgeService.subscribe(EVENT_TYPES.SUGGESTION_PROCESSED, (eventData) => {
      if (eventData.status === 'accepted' || eventData.status === 'applied') {
        console.log('AccommodationList: Suggestion accepted/applied, refreshing list...');
        fetchAccommodation();
      }
    });

    // Subscribe to accommodation added — handles agent proposal approvals creating new accommodations
    const accommodationAddedSubscription = eventBridgeService.subscribe(EVENT_TYPES.ACCOMMODATION_ADDED, () => {
      console.log('AccommodationList: Accommodation added, refreshing list...');
      fetchAccommodation();
    });

    fetchAccommodation();

    // Cleanup subscriptions
    return () => {
      if (cityChangeSubscription && typeof cityChangeSubscription.unsubscribe === 'function') {
        cityChangeSubscription.unsubscribe();
      }
      if (placeLinkSubscription && typeof placeLinkSubscription.unsubscribe === 'function') {
        placeLinkSubscription.unsubscribe();
      }
      if (suggestionProcessedSubscription && typeof suggestionProcessedSubscription.unsubscribe === 'function') {
        suggestionProcessedSubscription.unsubscribe();
      }
      if (accommodationAddedSubscription && typeof accommodationAddedSubscription.unsubscribe === 'function') {
        accommodationAddedSubscription.unsubscribe();
      }
    };
  }, [currentUser, tripId]);

  /**
     * Prepares the edit modal with the selected accommodation data
     * @param {Object} accommodationItem - The accommodation item to edit
     */
  const handleEditAccommodation = (accommodationItem) => {
    setEditingAccommodation(accommodationItem);
    setShowAddModal(true);
  };

  /**
     * Handles both creation and updating of accommodation items
     * Determines operation type based on editingAccommodation state
     * @param {Object} accommodationData - The accommodation data to save
     */
  const handleAddAccommodation = async (accommodationData) => {
    try {
      console.log('Adding accommodation with data:', accommodationData);

      if (editingAccommodation) {
        // Preserve existing summary if not provided in update
        if (!accommodationData.summary && editingAccommodation.summary) {
          accommodationData.summary = editingAccommodation.summary;
        }

        await accommodationService.updateAccommodation(effectiveUserId, tripId, editingAccommodation.id, accommodationData);
        console.log('Accommodation updated successfully, fetching updated list');
      } else {
        await accommodationService.createAccommodation(effectiveUserId, tripId, accommodationData);
        console.log('Accommodation added successfully, fetching updated list');
      }

      fetchAccommodation();
      setShowAddModal(false);
      setEditingAccommodation(null);
    } catch (error) {
      setError('Failed to create accommodation item. Please try again.');
      console.error('Error creating accommodation:', error);
    }
  };

  /**
     * Resets modal state and clears editing item
     */
  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingAccommodation(null);
  };

  /**
     * Handles deletion of accommodation items with user confirmation
     * @param {string} accommodationId - ID of the accommodation to delete
     */
  const handleDeleteAccommodation = async (accommodationId) => {
    if (window.confirm('Are you sure you want to delete this accommodation item?')) {
      try {
        await accommodationService.deleteAccommodation(effectiveUserId, tripId, accommodationId);
        await fetchAccommodation();
      } catch (err) {
        setError('Failed to delete accommodation item. Please try again.');
        console.error('Error deleting accommodation:', err);
      }
    }
  };

  /**
     * Formats check-in and check-out dates into a readable string
     * @param {Object} details - Object containing checkInDate and checkOutDate
     * @returns {string} Formatted date range or "Date not set" if dates are missing
     */
  const formatDateRange = (details) => {
    if (!details) return 'Date not set';
        
    const checkInDate = details.checkInDate;
    const checkOutDate = details.checkOutDate;

    if (!checkInDate) return 'Date not set';
        
    const formatDate = (date) => new Date(date).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });

    return checkOutDate && checkOutDate !== checkInDate 
      ? `${formatDate(checkInDate)} - ${formatDate(checkOutDate)}`
      : formatDate(checkInDate);
  };

  /**
     * Returns the appropriate icon component based on accommodation type
     * @param {string} type - The type of accommodation
     * @returns {ReactComponent} Icon component from react-icons/fa
     */
  const getAccommodationIcon = (type) => {
    switch (type) {
    case 'hotel':
      return <FaHotel />;
    case 'apartment':
      return <FaBuilding />;
    case 'house':
      return <FaHome />;
    case 'resort':
      return <FaUmbrellaBeach />;
    case 'hostel':
      return <FaBed />;
    default:
      return <FaQuestion />;
    }
  };

  /**
   * Generates the display summary for an accommodation item
   * Uses place data if the accommodation is linked to a place, otherwise uses stored summary
   * For city-less accommodations, shows only the accommodation name
   * @param {Object} item - The accommodation item
   * @returns {string} Display summary for the accommodation
   */
  const getDisplaySummary = (item) => {
    // If accommodation is linked to a place, use place data
    if (item.placeReference?.placeId && placesData[item.placeReference.placeId]) {
      const place = placesData[item.placeReference.placeId];
      const placeName = place.fullName || place.name;
      
      if (placeName) {
        // Extract hotel name from original summary or use accommodation name
        const hotelName = item.details?.name || item.summary?.split(':')[0]?.trim() || 'Accommodation';
        
        return `${hotelName}: ${placeName}`;
      }
    }
    
    // Check if there's a city in the stored data
    if (item.details?.city && item.details.city.trim() !== '') {
      const hotelName = item.details?.name || 'Accommodation';
      return `${hotelName}: ${item.details.city}`;
    }
    
    // No place or city data - return just the accommodation name
    return item.details?.name || item.summary || 'Accommodation';
  };

  // Loading State Display
  if (loading) {
    return <div className="loading">Loading accommodation items...</div>;
  }

  // Main Component Render
  return (
    <div className="accommodation-content">
      {/* Error Message Display */}
      {error && <div className="error-message">{error}</div>}

      {/* Header with Add Button */}
      <div className="accommodation-header">
        <button 
          onClick={() => setShowAddModal(true)}
          className="new-accommodation-button"
        >
          <FaPlus /> Add Accommodation
        </button>
      </div>

      {/* Empty State or Accommodation Grid */}
      {accommodation.length === 0 ? (
        <div className="no-accommodation">
          <div className="no-accommodation-icon">🏨</div>
          <p>No accommodation items yet! Click "Add Accommodation" to start planning.</p>
        </div>
      ) : (
        <div className="accommodation-grid">
          {/* Accommodation Item Cards */}
          {accommodation.map(item => (
            <div key={item.id} className="accommodation-item">
              <div className="accommodation-info">
                <Link 
                  to={`/trips/${tripId}/accommodation/${item.id}`}
                  className="accommodation-summary"
                >
                  <span className="accommodation-type-icon">
                    {getAccommodationIcon(item.type)}
                  </span>
                  {getDisplaySummary(item)}
                </Link>
                <div className="accommodation-meta">
                  <span>{formatDateRange(item.details)}</span>
                  <span className={`accommodation-status status-${item.status?.toLowerCase() || 'planned'}`}>
                    {item.status || 'Planned'}
                  </span>
                </div>
              </div>
              {/* Action Buttons */}
              <div className="action-buttons">
                <button
                  onClick={() => handleEditAccommodation(item)}
                  className="action-button action-button--edit"
                  title="Edit accommodation"
                  aria-label="Edit accommodation"
                >
                  <FaEdit />
                </button>
                <button
                  onClick={() => handleDeleteAccommodation(item.id)}
                  className="action-button action-button--delete"
                  title="Delete accommodation"
                  aria-label="Delete accommodation"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <AddAccommodation
        isOpen={showAddModal}
        onClose={handleCloseModal}
        onSave={handleAddAccommodation}
        initialAccommodation={editingAccommodation}
      />
    </div>
  );
}

export default AccommodationList; 